import React from 'react';
import { connect } from 'react-redux';
import FieldRow from '../../../FieldRow';
import Button from '../../../Button';
import * as actions from '../../../../actions/types';
import { getUserWalletAddCardInfo } from '../../../../reducers/user/wallet-cards';
import BottomPanel from '../../../BottomPanel';
import styled from 'styled-components';
import BackButton from '../../../common/BackButton';
import { dialogAlert } from '../../../../actions/creators/dialogs';
import { CARD_TYPE } from '../../../../types/card-type';
import { validateDateBiggerThan } from '../../../../utils/validation';
import { formatDateDmy } from '../../../../utils/dates';

const FormContainer = styled.form`
    max-width: 40rem;
`;

const Card = styled.img`
    width: 100%;
    height: auto;
`;

interface UserWalletAddCardProps {
    cardInfo: any | null;
    alert: (message: string) => void;
    cancel: () => void;
    confirm: (params: { type: string; cardNumber: string, pin: string, expirationDate: string }) => void;
    validate: (params: { type: string; cardNumber: string,  pin: string, expirationDate: string }) => void;
}

const UserWalletAddCard: React.FC<UserWalletAddCardProps> = ({ cardInfo, alert, cancel, confirm, validate }) => {
    const [cardType, setCardType] = React.useState<CARD_TYPE | null>(null);
    const [cardNumber, setCardNumber] = React.useState<string>('');
    const [pin, setPin] = React.useState<string>('');
    const [expirationDate, setExpirationDate] = React.useState<string>('');

    const handleExpirationDateChange = (value: string) => {
        value = formatDateDmy(value);
        setExpirationDate(value);
    };
  
    const handleCardTypeChange = (value: CARD_TYPE) => {
        setCardType(value);
        // Clear expiration date and pin on card type change
        setPin("");
        setExpirationDate("");
    };

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (!cardType) {
            alert('Por favor selecciona un tipo de tarjeta.');
            return;
        }

        if (!/^\d{16}$/.test(cardNumber)) {
            alert('Por favor ingresa un número de tarjeta válido.');
            return;
        }

        if (CARD_TYPE.MOVIECARD === cardType && !/^\d{3,4}$/.test(pin)) {
            alert('Por favor ingresa un PIN válido.');
            return;
        }

        if ([CARD_TYPE.TICKETCARD, CARD_TYPE.CINEFAN].includes(cardType)) {
            if (!expirationDate) {
                alert("Por favor ingresa una fecha de vencimiento.");
                return;
            }
        
            if (!validateDateBiggerThan(expirationDate, new Date())) {
                alert("Por favor ingresa una fecha de vencimiento válida.");
                return;
            }
        }

        validate({ type: cardType, cardNumber, pin, expirationDate });
    }

    return <>
        <div>
            <BackButton backTo="/usuario/wallet" label="Agregar tarjeta física" />

            <p className="my-5">
                <strong>Vincula aquí tu tarjeta</strong> ingresando los datos correspondientes.
            </p>

            <FormContainer onSubmit={onSubmit}>
                <FieldRow label="Tipo de tarjeta"
                    choices={[
                        { id: CARD_TYPE.MOVIECARD, name: 'MovieCard' },
                        // { id: CARD_TYPE.TICKETCARD, name: 'TicketCard' },
                        // { id: CARD_TYPE.CINEFAN, name: 'Cinefan' },
                    ]}
                    onChange={e => handleCardTypeChange(e.target.value as CARD_TYPE)}
                    required
                />
                <FieldRow value={cardNumber} onChange={e => setCardNumber(e.target.value)}
                    label="Número de tarjeta"
                    minLength={16}
                    maxLength={16}
                    required
                />
                {cardType && CARD_TYPE.MOVIECARD === cardType && <FieldRow value={pin} onChange={e => setPin(e.target.value)}
                    label="Ingresa tu PIN"
                    minLength={3}
                    maxLength={4}
                    required
                />}
                {cardType && [CARD_TYPE.CINEFAN, CARD_TYPE.TICKETCARD].includes(cardType) && (
                    <FieldRow
                        value={expirationDate}
                        onChange={(e) => handleExpirationDateChange(e.target.value)}
                        label="Fecha de vencimiento"
                        placeholder="dd/mm/yyyy"
                        required
                    />
                )}
                <Button primary block submit className="mt-5">Confirmar</Button>
            </FormContainer>
        </div>
        
        {cardInfo && cardType && (
            <BottomPanel open={true} onClose={cancel} title="Confirma que es tu tarjeta">
                <p>Por favor ayúdanos a verificar la información para finalizar la vinculación.</p>
                <Card src={cardInfo.image} alt={cardInfo.name} className="mb-4"/>
                <Button primary block onClick={() => confirm({ type: cardType, cardNumber, pin, expirationDate })} className="mb-3">
                    Confirmar vinculación
                </Button>
                <Button block onClick={cancel}>
                    Regresar
                </Button>
            </BottomPanel>
        )}
    </>;
};

const mapStateToProps = (state: any) => ({
    cardInfo: getUserWalletAddCardInfo(state)
});

const mapDispatchToProps = {
    alert: (message: string) => dialogAlert(message),
    cancel: () => ({ type: actions.USER_WALLET_ADD_CARD_CANCEL }),
    confirm: params => ({ type: actions.USER_WALLET_ADD_CARD_CONFIRM, payload: params }),
    validate: params => ({ type: actions.USER_WALLET_ADD_CARD_VALIDATE, payload: params }),
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(UserWalletAddCard);